import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../api/axiosConfig';
import { getToken as getStoredToken, setToken as storeToken, clearToken as clearStoredToken } from '../api/tokenStorage';

const AuthContext = createContext(null);

// One-time cleanup of stored attribute-import mappings. Bumped after the May 2026
// attribute overhaul so existing browsers drop stale mappings on their next login.
const MAPPING_CLEANUP_FLAG = 'rdh_mappings_cleared_v1';
const MAPPING_STORAGE_KEYS = ['attr-review-mappings', 'enrichment-attr-mappings'];

function clearStoredMappingsOnce() {
  try {
    if (localStorage.getItem(MAPPING_CLEANUP_FLAG)) return;
    for (const key of MAPPING_STORAGE_KEYS) {
      localStorage.removeItem(key);
    }
    localStorage.setItem(MAPPING_CLEANUP_FLAG, String(Date.now()));
  } catch (e) {
    // localStorage unavailable — ignore
  }
}

// Role hierarchy: super_admin > admin > research_gis > research_editor > research_viewer
const ROLE_LEVELS = {
  super_admin: 5,
  admin: 4,
  research_gis: 3,
  research_editor: 2,
  research_viewer: 1,
};

// Human-readable messages for SSO failures reported via the URL fragment.
const SSO_ERROR_MESSAGES = {
  not_provisioned: 'Your NYC account is not authorized for the Research Data Hub. Please contact an administrator.',
  account_disabled: 'Your account is disabled. Please contact an administrator.',
  session_expired: 'Your sign-in session expired. Please try again.',
  auth_failed: 'NYC sign-in failed. Please try again.',
  no_email: 'Your NYC account did not return an email address. Please contact an administrator.',
};

// Read a one-time SSO result from the URL fragment (#sso_token=… / #sso_error=…)
// left by the backend callback redirect. Pure read; the fragment is scrubbed in
// a mount effect to avoid side effects during render.
function readSsoFragment() {
  const hash = window.location.hash || '';
  if (!hash.includes('sso_token=') && !hash.includes('sso_error=')) return null;
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return { token: params.get('sso_token'), error: params.get('sso_error') };
}

export function AuthProvider({ children }) {
  const ssoResultRef = React.useRef(undefined);
  if (ssoResultRef.current === undefined) ssoResultRef.current = readSsoFragment();
  const ssoResult = ssoResultRef.current;

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => ssoResult?.token || getStoredToken());
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState(() =>
    ssoResult?.error ? (SSO_ERROR_MESSAGES[ssoResult.error] || 'NYC sign-in failed. Please try again.') : null
  );

  // Validate stored token on mount
  useEffect(() => {
    // A fresh SSO result arrived in the URL fragment — persist any token, then
    // scrub the fragment so it doesn't linger in history or survive a reload.
    if (ssoResult) {
      if (ssoResult.token) {
        storeToken(ssoResult.token);
        clearStoredMappingsOnce();
      }
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (!token) {
      setLoading(false);
      return;
    }
    axios.get('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setUser(res.data.data))
      .catch(() => {
        clearStoredToken();
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for forced logout (401 from axios interceptor)
  useEffect(() => {
    const handleLogout = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await axios.post('/api/v1/auth/login', { email, password });
    const data = res.data.data;

    // 2FA required — return intermediate state for the UI
    if (data.status === 'verification_required') {
      return {
        verificationRequired: true,
        verificationToken: data.verificationToken,
        phoneLast4: data.phoneLast4,
      };
    }

    // Direct login (SKIP_2FA mode)
    const { token: newToken, user: userData } = data;
    storeToken(newToken);
    clearStoredMappingsOnce();
    setToken(newToken);
    setUser(userData);
    return { verificationRequired: false, user: userData };
  }, []);

  const verifyCode = useCallback(async (verificationToken, code) => {
    const res = await axios.post('/api/v1/auth/verify-code', { verificationToken, code });
    const { token: newToken, user: userData } = res.data.data;
    storeToken(newToken);
    clearStoredMappingsOnce();
    setToken(newToken);
    setUser(userData);
    return userData;
  }, []);

  // Redirect to the backend, which kicks off the NYC Entra ID OIDC flow.
  const loginWithNyc = useCallback(() => {
    const apiBase = process.env.REACT_APP_API_URL || '';
    window.location.href = `${apiBase}/api/v1/auth/sso/login`;
  }, []);

  const clearSsoError = useCallback(() => setSsoError(null), []);

  const logout = useCallback(() => {
    clearStoredToken();
    setToken(null);
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await axios.get('/api/v1/auth/me');
    setUser(res.data.data);
  }, []);

  // Role checks
  const hasMinRole = useCallback((minRole) => {
    if (!user) return false;
    return (ROLE_LEVELS[user.role] || 0) >= (ROLE_LEVELS[minRole] || 0);
  }, [user]);

  const isAdmin = useCallback(() => user?.role === 'admin' || user?.role === 'super_admin', [user]);
  const isSuperAdmin = useCallback(() => user?.role === 'super_admin', [user]);
  const canEdit = useCallback(() => hasMinRole('research_editor'), [hasMinRole]);
  const canCreateProject = useCallback(() => hasMinRole('research_editor'), [hasMinRole]);
  const canUseGIS = useCallback(() => hasMinRole('research_gis'), [hasMinRole]);

  const value = {
    user,
    token,
    loading,
    ssoError,
    login,
    verifyCode,
    loginWithNyc,
    clearSsoError,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isAdmin,
    isSuperAdmin,
    canEdit,
    canCreateProject,
    canUseGIS,
    hasMinRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
